import { Button, Heading, Text, VStack } from "@chakra-ui/react"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import classnames from "classnames"
import React, { useState } from "react"
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary"

interface LoadingChunkErrorBoundaryProps {
  className?: string
  children: React.ReactNode
}

interface ChunkError extends Error {
  type?: string
  request?: any
}

const LoadingChunkErrorBoundary: React.FC<LoadingChunkErrorBoundaryProps> = ({
  className,
  children
}) => {
  const [showErrorDetails, setShowErrorDetails] = useState(false)
  const { t } = useTranslate()

  const handleError = (error: Error) => {
    // Check if it's a loading chunk error
    const res = [
      /Loading chunk [0-9a-zA-Z-_]+ failed\./,
      /Loading CSS chunk [0-9a-zA-Z-_]+ failed\./
    ]

    if (!res.find((re) => re.test(error.message))) {
      // When not a loading chunk error, propagate the error to a parent
      // ErrorBoundary component as otherwise React will attempt to rerender this tree
      // and the error will be thrown again and again (at least in local dev).
      throw error
    }
  }

  const ErrorFallback = ({ error }: { error: ChunkError }) => {
    return (
      <VStack
        className={classnames("LoadingChunkErrorBoundary", className)}
        w="100%"
        minH="100%"
        maxH="100vh"
        justifyContent="center"
        alignItems="center"
        bg="white"
        p={2}
        gap={4}
      >
        <Pathicon icon="rocket" style={{ fontSize: "2em" }} />
        <Heading
          as="h1"
          // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
          size="h4"
          m={0}
        >
          {t("Updates available")}
        </Heading>
        <Text m={0}>{t("A newer version is available. Please refresh.")}</Text>
        {showErrorDetails && (
          <Text
            fontFamily="monospace"
            textAlign="left"
            m={0}
            p={4}
            borderRadius="md"
            color="black"
            bg="gray.200"
            maxW="min(600px, 100%)"
            overflow="auto"
          >
            {JSON.stringify(
              {
                message: error.message,
                name: error.name,
                type: error.type,
                request: error.request
              },
              null,
              2
            )}
          </Text>
        )}
        <Button
          colorPalette="brand"
          variant="solid"
          cursor="pointer"
          onClick={() => {
            // Add a query param to tell the browser to load the page from the server
            // not from the cache. It seems sometimes the page is cached and users still
            // see this message, hence this workaround. We remove the query param on page load.
            const url = new URL(window.location.href)
            url.searchParams.append(
              "forceHardRefresh",
              String(new Date().getTime())
            )
            window.location.href = url.toString()
          }}
        >
          {t("Refresh")}
        </Button>
        {!showErrorDetails && (
          <Button
            variant="plain"
            p={0}
            bg="none"
            cursor="pointer"
            onClick={() => setShowErrorDetails(true)}
          >
            {t("Expand details")}
          </Button>
        )}
      </VStack>
    )
  }

  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  )
}

LoadingChunkErrorBoundary.displayName = "LoadingChunkErrorBoundary"

export default LoadingChunkErrorBoundary
